import { LoadingOutlined } from '@ant-design/icons';
import { Image, Progress, Tooltip } from 'antd';
import clsx from 'clsx';
import { FlipHorizontal2, FlipVertical2 } from 'lucide-react';
import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FC,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FilesImagePreviewContext } from '../../../hooks/useFilesImagePreviewSrc';
import useOpenFile from '../../../hooks/useOpenFile';
import type File from '../../../interfaces/File';
import {
  fetchFileFullPreviewUrl,
  FILES_IMAGE_PREVIEW_FILE_ID_ATTR,
  getCachedFullPreviewUrl,
  IMAGE_PREVIEW_FULL_AUTO_MAX_BYTES,
  registerFilesImagePreviewOpener,
} from '../../../utils/filePreview';
import { Icon } from '../../Core/Icon';
import {
  buildOrderedPreviewFileIds,
  isPreviewableImageExtension,
  shouldShowLoadFullQualityButton,
} from './filesImagePreviewHelpers';

/** Matches Ant Design preview action icon sizing (`font-size` on the action button). */
const PREVIEW_TOOLBAR_ICON_CLASS_NAME = 'h-[1em] w-[1em]';

/**
 * Viewport caps for preview images.
 *
 * Ant Design uses `max-height: 70%` / `max-width: 100%` on `.ant-image-preview-img`, which only
 * works when the img is a direct child of the full-viewport preview body. Our `imageRender`
 * wrapper (needed for HD / progress overlays) makes those percentages resolve against an
 * indefinite height, so full-res images paint at natural size and cover the screen. Use vh/vw
 * so the default fit stays inside the viewport with close + toolbar visible; zoom still scales
 * via the existing transform on the img.
 */
const PREVIEW_IMAGE_VIEWPORT_STYLE: CSSProperties = {
  maxHeight: '70vh',
  maxWidth: '90vw',
};

/**
 * Ant Design Image.PreviewGroup always injects its own `@ant-design/icons` set and overwrites
 * `preview.icons`, so the toolbar must be rebuilt via `actionsRender` with Lucide icons.
 */
function renderPreviewToolbarAction(options: {
  disabled?: boolean;
  icon: ReactNode;
  onClick: () => void;
  type: string;
}): ReactElement {
  const { disabled = false, icon, onClick, type } = options;
  return (
    <button
      aria-label={type}
      className={clsx(
        'ant-image-preview-actions-action',
        disabled && 'ant-image-preview-actions-action-disabled',
      )}
      disabled={disabled}
      key={type}
      onClick={onClick}
      type="button"
    >
      {icon}
    </button>
  );
}

/**
 * Syncs full-resolution fetch to the slide Ant Design is actually rendering.
 *
 * `@rc-component/image` PreviewGroup calls `onOpenChange(true, { current })` with a *stale*
 * index (setCurrent has not flushed yet), so open-time HD fetch must not trust that value.
 */
function PreviewSlideFullResSync({
  fileId,
  onActiveFileId,
}: {
  fileId: number | undefined;
  onActiveFileId: (fileId: number) => void;
}): null {
  useEffect(() => {
    if (fileId == null) return;
    onActiveFileId(fileId);
  }, [fileId, onActiveFileId]);
  return null;
}

/** Props for {@link FilesImagePreviewGroup}. */
interface FilesImagePreviewGroupProps {
  children: ReactNode;
  files: File[];
}

/**
 * Resolves the percentage shown in the full-resolution download overlay.
 * Prefers the transfer-layer percent; otherwise derives it from received and total bytes.
 */
function resolveDownloadPercent(
  storedPercent: number | undefined,
  receivedBytes: number,
  totalBytes?: number,
): number | undefined {
  if (storedPercent != null) return storedPercent;
  if (totalBytes && totalBytes > 0) {
    return Math.min(99, Math.round((receivedBytes / totalBytes) * 100));
  }
  return undefined;
}

/**
 * Coordinates thumbnail previews across list and table views inside one `Image.PreviewGroup`.
 *
 * Child `FileIcon` instances register thumbnail URLs for the carousel. Image files below
 * {@link IMAGE_PREVIEW_FULL_AUTO_MAX_BYTES} auto-fetch full resolution on the active slide;
 * larger images show an HD control overlaid on the preview image. A centered overlay reports
 * download progress without hiding the thumbnail.
 *
 * Full-res fetch is keyed off `imageRender`'s `current` (via {@link PreviewSlideFullResSync}),
 * not PreviewGroup `onOpenChange`, because `@rc-component/image` reports a stale index on open.
 *
 * `previewConfig` is kept referentially stable (handlers via refs) so progress re-renders do not
 * remount the preview and jump to the wrong slide.
 */
const FilesImagePreviewGroup: FC<FilesImagePreviewGroupProps> = ({ files, children }) => {
  const { t } = useTranslation();
  const { openFileSafely } = useOpenFile();
  const [fullPreviewUrlByFileId, setFullPreviewUrlByFileId] = useState<Record<number, string>>({});
  const [thumbnailUrlByFileId, setThumbnailUrlByFileId] = useState<Record<number, string>>({});
  const [loadingFileIds, setLoadingFileIds] = useState<Set<number>>(() => new Set());
  const [fullPreviewProgressByFileId, setFullPreviewProgressByFileId] = useState<
    Record<number, number | undefined>
  >({});

  const filesById = useMemo(() => new Map(files.map((file) => [file.id, file])), [files]);

  const orderedPreviewFileIds = useMemo(
    () => buildOrderedPreviewFileIds(files, thumbnailUrlByFileId),
    [files, thumbnailUrlByFileId],
  );

  /**
   * While the carousel is open, freeze the slide list so late-loading thumbnails cannot
   * shift indices and show a different file than the one that was clicked.
   */
  const [frozenPreviewFileIds, setFrozenPreviewFileIds] = useState<number[] | null>(null);
  const activeOrderedPreviewFileIds = frozenPreviewFileIds ?? orderedPreviewFileIds;

  const orderedPreviewFileIdsRef = useRef(activeOrderedPreviewFileIds);
  orderedPreviewFileIdsRef.current = activeOrderedPreviewFileIds;

  const filesByIdRef = useRef(filesById);
  filesByIdRef.current = filesById;

  /**
   * Retrieves the full preview source URL for a file ID.
   * @param fileId The ID of the file to retrieve the full preview source URL for.
   * @returns The full preview source URL for the file, or `undefined` if not found.
   */
  const getFullPreviewSrc = useCallback(
    (fileId: number) => fullPreviewUrlByFileId[fileId] ?? getCachedFullPreviewUrl(fileId),
    [fullPreviewUrlByFileId],
  );

  /**
   * Registers a thumbnail URL for a file ID (images only).
   * @param fileId The ID of the file to register the thumbnail URL for.
   * @param thumbnailUrl The thumbnail URL to register.
   */
  const registerPreviewableImage = useCallback((fileId: number, thumbnailUrl: string) => {
    const file = filesByIdRef.current.get(fileId);
    if (file && !isPreviewableImageExtension(file.fileExtension)) return;

    setThumbnailUrlByFileId((previous) => {
      if (previous[fileId] === thumbnailUrl) return previous;
      return { ...previous, [fileId]: thumbnailUrl };
    });
  }, []);

  /**
   * Resolves the preview source URL for a file ID.
   * @param fileId The ID of the file to resolve the preview source URL for.
   * @returns The preview source URL for the file, or `undefined` if not found.
   */
  const resolvePreviewSrc = useCallback(
    (fileId: number) =>
      fullPreviewUrlByFileId[fileId] ??
      getCachedFullPreviewUrl(fileId) ??
      thumbnailUrlByFileId[fileId],
    [fullPreviewUrlByFileId, thumbnailUrlByFileId],
  );

  /**
   * Builds the preview items array for the carousel.
   * Always uses thumbnail URLs so Image click matching (`src`) stays stable after HD fetch.
   * Full-resolution URLs are applied in `imageRender` via {@link resolvePreviewSrc}.
   * @returns The preview items array for the carousel.
   */
  const previewItems = useMemo(
    () =>
      activeOrderedPreviewFileIds.map((fileId) => ({
        src: thumbnailUrlByFileId[fileId] ?? '',
      })),
    [activeOrderedPreviewFileIds, thumbnailUrlByFileId],
  );

  /**
   * Clears the full preview progress for a file ID.
   * @param fileId The ID of the file to clear the full preview progress for.
   */
  const clearFullPreviewProgress = useCallback((fileId: number) => {
    setFullPreviewProgressByFileId((previous) => {
      if (!(fileId in previous)) return previous;
      const next = { ...previous };
      delete next[fileId];
      return next;
    });
  }, []);

  /**
   * Loads the full preview for a file.
   * @param file The file to load the full preview for.
   */
  const loadFullPreview = useCallback(
    async (file: File) => {
      if (fullPreviewUrlByFileId[file.id] || loadingFileIds.has(file.id)) return;

      // Session cache survives table↔grid remounts of this group; hydrate React state from it.
      const cachedUrl = getCachedFullPreviewUrl(file.id);
      if (cachedUrl) {
        setFullPreviewUrlByFileId((previous) => ({ ...previous, [file.id]: cachedUrl }));
        return;
      }

      setLoadingFileIds((previous) => new Set(previous).add(file.id));
      setFullPreviewProgressByFileId((previous) => ({ ...previous, [file.id]: undefined }));
      try {
        const url = await fetchFileFullPreviewUrl(
          file.id,
          file.fileSize,
          file.fileExtension,
          (progress) => {
            setFullPreviewProgressByFileId((previous) => ({
              ...previous,
              [file.id]: resolveDownloadPercent(
                progress.percent,
                progress.receivedBytes,
                progress.totalBytes,
              ),
            }));
          },
        );
        setFullPreviewUrlByFileId((previous) => ({ ...previous, [file.id]: url }));
      } catch {
        // Keep the thumbnail visible when full-resolution fetch fails.
      } finally {
        setLoadingFileIds((previous) => {
          const next = new Set(previous);
          next.delete(file.id);
          return next;
        });
        clearFullPreviewProgress(file.id);
      }
    },
    [clearFullPreviewProgress, fullPreviewUrlByFileId, loadingFileIds],
  );

  /**
   * Ensures full-resolution for the slide Ant Design is rendering (`info.current`).
   * Must not be driven by PreviewGroup `onOpenChange`'s `current` — that value is stale
   * on open (setCurrent has not committed yet), which skipped HD fetch for non-first slides.
   */
  const ensureFullPreviewForActiveFile = useCallback(
    (fileId: number) => {
      const file = filesByIdRef.current.get(fileId);
      if (!file || !isPreviewableImageExtension(file.fileExtension)) return;

      // Large files: only hydrate from session cache (no auto network fetch).
      if (file.fileSize >= IMAGE_PREVIEW_FULL_AUTO_MAX_BYTES) {
        if (getCachedFullPreviewUrl(file.id)) {
          void loadFullPreview(file);
        }
        return;
      }
      void loadFullPreview(file);
    },
    [loadFullPreview],
  );

  const ensureFullPreviewForActiveFileRef = useRef(ensureFullPreviewForActiveFile);
  ensureFullPreviewForActiveFileRef.current = ensureFullPreviewForActiveFile;

  /** Stable callback for {@link PreviewSlideFullResSync} (avoids effect churn on load state). */
  const onActivePreviewFileId = useCallback((fileId: number) => {
    ensureFullPreviewForActiveFileRef.current(fileId);
  }, []);

  /**
   * Opens the carousel for `fileId` by clicking its registered thumbnail Image.
   * Programmatic `preview.open`/`current` is unreliable in `@rc-component/image` because reopen
   * resets the slide unless `keepOpenIndex` is set (only the native Image click path does that).
   * @returns `true` when a previewable thumbnail trigger was found and clicked.
   */
  const openPreview = useCallback(
    (fileId: number): boolean => {
      if (!activeOrderedPreviewFileIds.includes(fileId)) return false;
      if (typeof document === 'undefined') return false;

      const imageTrigger = document.querySelector(
        `[${FILES_IMAGE_PREVIEW_FILE_ID_ATTR}="${fileId}"] .ant-image`,
      );
      if (!(imageTrigger instanceof HTMLElement)) return false;

      imageTrigger.click();
      return true;
    },
    [activeOrderedPreviewFileIds],
  );

  useEffect(() => {
    registerFilesImagePreviewOpener(openPreview);
    return () => {
      registerFilesImagePreviewOpener(null);
    };
  }, [openPreview]);

  const contextValue = useMemo(
    () => ({ getFullPreviewSrc, openPreview, registerPreviewableImage }),
    [getFullPreviewSrc, openPreview, registerPreviewableImage],
  );

  /**
   * Renders the preview image, optional HD control overlaid on the image, and download progress.
   * HD sits on the image (not below it) so Ant Design's centered `max-height: 70%` body cannot
   * push the control off-screen.
   */
  const renderPreviewImageWithProgress = useCallback(
    (originalNode: ReactElement, fileId: number | undefined): React.ReactNode => {
      const previewSrc = fileId != null ? resolvePreviewSrc(fileId) : undefined;
      if (!previewSrc || !isValidElement(originalNode)) {
        return (
          <>
            <PreviewSlideFullResSync fileId={fileId} onActiveFileId={onActivePreviewFileId} />
            {originalNode}
          </>
        );
      }

      const originalPreviewProps = originalNode.props as {
        className?: string;
        key?: string;
        src?: string;
        style?: CSSProperties;
      };
      const previewImage = cloneElement(
        originalNode as ReactElement<{
          className?: string;
          key?: string;
          src?: string;
          style?: CSSProperties;
        }>,
        {
          key: previewSrc,
          src: previewSrc,
          // Preserve transform (zoom/pan/rotate); override broken % max-* with viewport units.
          style: {
            ...originalPreviewProps.style,
            ...PREVIEW_IMAGE_VIEWPORT_STYLE,
          },
        },
      );

      const file = fileId != null ? filesById.get(fileId) : undefined;
      const isDownloadingFullRes = fileId != null && loadingFileIds.has(fileId);
      const hasFullRes =
        file != null &&
        (Boolean(fullPreviewUrlByFileId[file.id]) || Boolean(getCachedFullPreviewUrl(file.id)));
      const showLoadFullQuality = shouldShowLoadFullQualityButton(
        file,
        hasFullRes,
        isDownloadingFullRes,
      );
      const downloadPercent = fileId != null ? fullPreviewProgressByFileId[fileId] : undefined;

      return (
        <div className="max-h-70vh relative inline-flex max-w-[90vw] items-center justify-center">
          <PreviewSlideFullResSync fileId={fileId} onActiveFileId={onActivePreviewFileId} />
          {previewImage}
          {isDownloadingFullRes && (
            <span
              aria-live="polite"
              className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
            >
              <span className="rounded-full bg-black/55 p-1">
                <Progress
                  aria-label={t('imagePreview.loadingFullQuality')}
                  format={(percentValue) =>
                    percentValue != null ? `${Math.round(percentValue)}%` : <LoadingOutlined />
                  }
                  percent={downloadPercent}
                  size={52}
                  railColor="rgba(255,255,255,0.25)"
                  strokeColor="#ffffff"
                  type="circle"
                />
              </span>
            </span>
          )}
          {showLoadFullQuality && file != null && (
            // Full-width flex row (not left-1/2 shrink-to-fit) so a narrow image cannot squeeze the pill.
            <span className="pointer-events-none absolute inset-x-0 bottom-2 z-20 flex justify-center px-2">
              <Tooltip
                getPopupContainer={() => document.body}
                placement="top"
                styles={{ root: { zIndex: 2000 } }}
                title={t('imagePreview.loadFullQualityTooltip')}
              >
                <button
                  aria-label={t('imagePreview.loadFullQuality')}
                  className="pointer-events-auto flex w-max shrink-0 items-center gap-2 rounded-full bg-black/80 px-4 py-2.5 text-sm font-semibold whitespace-nowrap text-white shadow-lg hover:bg-black/90"
                  onClick={(event) => {
                    event.stopPropagation();
                    void loadFullPreview(file);
                  }}
                  type="button"
                >
                  <Icon className="h-5 w-5 shrink-0" name="HdIcon" />
                  <span className="whitespace-nowrap">{t('imagePreview.loadFullQuality')}</span>
                </button>
              </Tooltip>
            </span>
          )}
        </div>
      );
    },
    [
      filesById,
      fullPreviewProgressByFileId,
      fullPreviewUrlByFileId,
      loadFullPreview,
      loadingFileIds,
      onActivePreviewFileId,
      resolvePreviewSrc,
      t,
    ],
  );

  const renderPreviewImageWithProgressRef = useRef(renderPreviewImageWithProgress);
  renderPreviewImageWithProgressRef.current = renderPreviewImageWithProgress;

  const openFileSafelyRef = useRef(openFileSafely);
  openFileSafelyRef.current = openFileSafely;

  const tRef = useRef(t);
  tRef.current = t;

  /**
   * Stable preview config — must not recreate on progress ticks or HD URL updates, or
   * `@rc-component/image` remounts and can open/show the wrong slide.
   *
   * Toolbar is fully rebuilt: antd overwrites `preview.icons` with `@ant-design/icons`.
   */
  const previewConfig = useMemo(
    () => ({
      actionsRender: (
        _originalNode: ReactElement,
        info: {
          actions: {
            onClose: () => void;
            onFlipX: () => void;
            onFlipY: () => void;
            onRotateLeft: () => void;
            onRotateRight: () => void;
            onZoomIn: () => void;
            onZoomOut: () => void;
          };
          current: number;
          transform: { scale: number };
        },
      ): React.ReactNode => {
        const fileId = orderedPreviewFileIdsRef.current[info.current];
        const file = fileId != null ? filesByIdRef.current.get(fileId) : undefined;
        const { scale } = info.transform;

        return (
          <div className="ant-image-preview-actions">
            {renderPreviewToolbarAction({
              icon: (
                <FlipVertical2
                  aria-hidden
                  className={PREVIEW_TOOLBAR_ICON_CLASS_NAME}
                  strokeWidth={1}
                />
              ),
              onClick: info.actions.onFlipY,
              type: 'flipY',
            })}
            {renderPreviewToolbarAction({
              icon: (
                <FlipHorizontal2
                  aria-hidden
                  className={PREVIEW_TOOLBAR_ICON_CLASS_NAME}
                  strokeWidth={1}
                />
              ),
              onClick: info.actions.onFlipX,
              type: 'flipX',
            })}
            {renderPreviewToolbarAction({
              icon: <Icon className={PREVIEW_TOOLBAR_ICON_CLASS_NAME} name="RotateCcwIcon" />,
              onClick: info.actions.onRotateLeft,
              type: 'rotateLeft',
            })}
            {renderPreviewToolbarAction({
              icon: <Icon className={PREVIEW_TOOLBAR_ICON_CLASS_NAME} name="RotateCwIcon" />,
              onClick: info.actions.onRotateRight,
              type: 'rotateRight',
            })}
            {renderPreviewToolbarAction({
              disabled: scale <= 1,
              icon: <Icon className={PREVIEW_TOOLBAR_ICON_CLASS_NAME} name="ZoomOutIcon" />,
              onClick: info.actions.onZoomOut,
              type: 'zoomOut',
            })}
            {renderPreviewToolbarAction({
              disabled: scale >= 50,
              icon: <Icon className={PREVIEW_TOOLBAR_ICON_CLASS_NAME} name="ZoomInIcon" />,
              onClick: info.actions.onZoomIn,
              type: 'zoomIn',
            })}
            {file != null && (
              <Tooltip
                getPopupContainer={() => document.body}
                placement="top"
                styles={{ root: { zIndex: 2000 } }}
                title={tRef.current('imagePreview.openInViewer')}
              >
                {renderPreviewToolbarAction({
                  icon: (
                    <Icon
                      className={PREVIEW_TOOLBAR_ICON_CLASS_NAME}
                      name="SquareArrowOutUpRight"
                    />
                  ),
                  onClick: () => {
                    info.actions.onClose();
                    openFileSafelyRef.current(file, true, true);
                  },
                  type: 'openInViewer',
                })}
              </Tooltip>
            )}
          </div>
        );
      },
      imageRender: (originalNode: ReactElement, info: { current: number }): React.ReactNode => {
        const fileId = orderedPreviewFileIdsRef.current[info.current];
        return renderPreviewImageWithProgressRef.current(originalNode, fileId);
      },
      // HD fetch is driven by PreviewSlideFullResSync inside imageRender (correct `current`).
      // Do not call ensureFullPreview from onOpenChange — its `current` is stale on open.
      onOpenChange: (open: boolean) => {
        if (open) {
          setFrozenPreviewFileIds([...orderedPreviewFileIdsRef.current]);
          return;
        }
        setFrozenPreviewFileIds(null);
      },
    }),
    [],
  );

  /**
   * Renders the FilesImagePreviewGroup component.
   * @returns The FilesImagePreviewGroup component.
   */
  return (
    <FilesImagePreviewContext.Provider value={contextValue}>
      <Image.PreviewGroup items={previewItems} preview={previewConfig}>
        {children}
      </Image.PreviewGroup>
    </FilesImagePreviewContext.Provider>
  );
};

export default FilesImagePreviewGroup;
